import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OrgSettingsPanel } from '@/components/settings/OrgSettingsPanel';
import { BillingSettingsPanel } from '@/components/settings/BillingSettingsPanel';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Input, Badge, Alert, AlertDescription, Spinner, ConfirmDialog } from '@/components/ui';
import {
  User,
  Building,
  Bell,
  Shield,
  Palette,
  Database,
  Key,
  Mail,
  Camera,
  Upload,
  RefreshCw,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  Plus,
  Bot,
  MessageSquare,
  Settings as SettingsIcon,
  Search,
  CreditCard,
  Webhook,
  ShieldCheck,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { TimeZonePicker } from '@/components/ui/TimeZonePicker';
import { useDateFormat } from '@/hooks/useDateFormat';
import { AUTO_TIME_ZONE } from '@simcheck/shared-types';
import { ViewAsOrgButton } from '@/components/admin/ViewAsOrgButton';
import TwoFactorPanel from '@/components/security/TwoFactorPanel';
import { useTheme } from '@/providers/ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { doc, updateDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/config/firebase';
import { CreateApiKeyModal } from '@/components/api-keys';
import { CreateMcpKeyModal, McpKeyList, McpConfigExamples } from '@/components/mcp-keys';
import { WebhookList } from '@/components/webhooks';
import {
  listApiKeys,
  revokeApiKey,
  rotateApiKey,
  ApiKeyListItem
} from '@/services/apiKeyService';

type SettingsTab =
  | 'profile'
  | 'organization'
  | 'billing'
  | 'notifications'
  | 'two-factor'
  | 'security'
  | 'webhooks'
  | 'ai-integration';

const SETTINGS_TAB_IDS: SettingsTab[] = [
  'profile',
  'organization',
  'billing',
  'notifications',
  'two-factor',
  'security',
  'webhooks',
  'ai-integration',
];

// Friendly URL aliases → internal tab id (the API Keys tab's id is historically `security`).
const SETTINGS_TAB_ALIASES: Record<string, SettingsTab> = {
  'api-keys': 'security',
};

/** Resolve a `?tab=` query value (raw id or friendly alias) to a real tab id. */
function resolveSettingsTab(param: string | null): SettingsTab | null {
  if (!param) return null;
  if (SETTINGS_TAB_ALIASES[param]) return SETTINGS_TAB_ALIASES[param];
  if ((SETTINGS_TAB_IDS as string[]).includes(param)) return param as SettingsTab;
  return null;
}

export function Settings() {
  const { theme, toggleTheme } = useTheme();
  const { currentUser, userProfile, activeOrganization, refreshUserProfile, isSuperAdmin } = useAuth();
  const fmt = useDateFormat();
  const { zoneLabel } = fmt;
  const [tzSaving, setTzSaving] = useState(false);
  // The stored preference ('auto' / IANA name); absent === 'auto'.
  const savedTimezone = userProfile?.preferences?.timezone || AUTO_TIME_ZONE;

  const handleTimezoneChange = useCallback(async (tz: string) => {
    if (!currentUser || tz === savedTimezone) return;
    setTzSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { 'preferences.timezone': tz });
      await refreshUserProfile();
    } catch (err) {
      console.error('Failed to save timezone preference:', err);
    } finally {
      setTzSaving(false);
    }
  }, [currentUser, savedTimezone, refreshUserProfile]);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    () => resolveSettingsTab(searchParams.get('tab')) ?? 'profile'
  );

  // Keep the active tab in sync with the `?tab=` query param (deep-links from
  // the Developer Portal cards, command palette, etc.).
  useEffect(() => {
    const resolved = resolveSettingsTab(searchParams.get('tab'));
    if (resolved && resolved !== activeTab) {
      setActiveTab(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Org picker state (super admin)
  const [orgPickerList, setOrgPickerList] = useState<{ id: string; name: string }[]>([]);
  const [orgPickerLoading, setOrgPickerLoading] = useState(false);
  const [orgPickerSearch, setOrgPickerSearch] = useState('');
  const [showOrgPicker, setShowOrgPicker] = useState(false);

  // When a new org becomes active (selection completed), close the picker
  useEffect(() => {
    if (activeOrganization) {
      setShowOrgPicker(false);
      setOrgPickerSearch('');
    }
  }, [activeOrganization?.id]);

  // Load orgs when the picker is open and we don't have the list yet
  const pickerIsOpen = isSuperAdmin && (!activeOrganization || showOrgPicker);
  useEffect(() => {
    if (pickerIsOpen && activeTab === 'organization' && orgPickerList.length === 0 && !orgPickerLoading) {
      setOrgPickerLoading(true);
      const orgsQuery = query(collection(db, 'organizations'), orderBy('name', 'asc'));
      getDocs(orgsQuery)
        .then(snap => {
          setOrgPickerList(snap.docs.map(d => ({ id: d.id, name: d.data().name as string })));
        })
        .finally(() => setOrgPickerLoading(false));
    }
  }, [pickerIsOpen, activeTab]);

  // MCP Keys state
  const [createMcpKeyModalOpen, setCreateMcpKeyModalOpen] = useState(false);
  
  // Profile form state
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  
  // Update displayName when userProfile changes
  React.useEffect(() => {
    if (userProfile?.displayName) {
      setDisplayName(userProfile.displayName);
    }
  }, [userProfile?.displayName]);
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKeyListItem[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysError, setApiKeysError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rotatedKey, setRotatedKey] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  
  const handleSaveProfile = async () => {
    if (!currentUser || !userProfile) return;
    
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    
    try {
      // Update Firebase Auth display name
      await updateProfile(currentUser, {
        displayName: displayName
      });
      
      // Update Firestore user profile
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: displayName
      });
      
      setProfileSuccess('Profile updated successfully!');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setProfileError(error.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };
  
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Image must be less than 5MB');
      return;
    }
    
    setPhotoUploading(true);
    setPhotoError('');
    
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `avatars/${currentUser.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);
      
      // Update Firebase Auth photo URL
      await updateProfile(currentUser, { photoURL });
      
      // Update Firestore user profile
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { photoURL });
      
      await refreshUserProfile();
      setProfileSuccess('Profile photo updated successfully!');
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      setPhotoError(error.message || 'Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Fetch API keys when organization changes or when switching to security tab
  const fetchApiKeys = useCallback(async () => {
    if (!activeOrganization?.id) return;

    setApiKeysLoading(true);
    setApiKeysError('');

    try {
      const response = await listApiKeys(activeOrganization.id);
      setApiKeys(response.apiKeys);
    } catch (error: any) {
      console.error('Error fetching API keys:', error);
      setApiKeysError(error.message || 'Failed to load API keys');
    } finally {
      setApiKeysLoading(false);
    }
  }, [activeOrganization?.id]);

  // Load API keys when security tab is active
  useEffect(() => {
    if (activeTab === 'security' && activeOrganization?.id) {
      fetchApiKeys();
    }
  }, [activeTab, activeOrganization?.id, fetchApiKeys]);

  const handleRevokeKey = async () => {
    if (!selectedKeyId || !activeOrganization?.id) return;

    setActionLoading(true);
    try {
      await revokeApiKey(selectedKeyId, activeOrganization.id, 'User requested revocation');
      await fetchApiKeys();
      setRevokeDialogOpen(false);
      setSelectedKeyId(null);
    } catch (error: any) {
      console.error('Error revoking API key:', error);
      setApiKeysError(error.message || 'Failed to revoke API key');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRotateKey = async () => {
    if (!selectedKeyId || !activeOrganization?.id) return;

    setActionLoading(true);
    try {
      const response = await rotateApiKey(selectedKeyId, activeOrganization.id);
      setRotatedKey(response.key);
      await fetchApiKeys();
    } catch (error: any) {
      console.error('Error rotating API key:', error);
      setApiKeysError(error.message || 'Failed to rotate API key');
      setRotateDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyKeyPrefix = async (keyPrefix: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(keyPrefix);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Never';
    return fmt.formatDate(date);
  };

  const handleChangePassword = async () => {
    if (!currentUser || !currentUser.email) return;
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    
    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, newPassword);
      
      setPasswordSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else {
        setPasswordError(error.message || 'Failed to change password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };
  
  const getUserInitials = () => {
    if (userProfile?.displayName) {
      return userProfile.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    if (currentUser?.email) {
      return currentUser.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };
  
  // Grouped by blast radius, not by feature: a change under "Personal" affects
  // only the signed-in user, everything under the org group affects every member.
  // The org group is headed by the real org name so it's unambiguous *which* org
  // is being edited when a super admin is viewing-as another tenant.
  const tabGroups: { id: string; label: string; tabs: { id: SettingsTab; label: string; icon: LucideIcon }[] }[] = [
    {
      id: 'personal',
      label: 'Personal',
      tabs: [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'two-factor', label: 'Security', icon: ShieldCheck },
        { id: 'notifications', label: 'Notifications', icon: Bell },
      ],
    },
    {
      id: 'organization',
      label: activeOrganization?.name || 'Organization',
      tabs: [
        { id: 'organization', label: 'Organization', icon: Building },
        ...(activeOrganization ? [{ id: 'billing' as const, label: 'Billing', icon: CreditCard }] : []),
        { id: 'security', label: 'API Keys', icon: Key },
        { id: 'webhooks', label: 'Webhooks', icon: Webhook },
        { id: 'ai-integration', label: 'AI Integration', icon: Bot },
      ],
    },
  ];
  
  return (
    <AppShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-display font-semibold text-foreground mb-2">
            Settings
          </h1>
          <p className="text-body text-muted-foreground">
            Manage your personal account and your organization's configuration
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {/* Sidebar Tabs */}
          <div className="lg:col-span-1">
            <Card padding="sm">
              <nav className="space-y-4">
                {tabGroups.map(group => (
                  <div key={group.id} className="space-y-1">
                    <div
                      className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate"
                      title={group.label}
                    >
                      {group.label}
                    </div>
                    {group.tabs.map(tab => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded
                            transition-colors text-left
                            ${activeTab === tab.id
                              ? 'bg-accent text-accent-blue border-l-2 border-accent-blue'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }
                          `}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-sm font-medium">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </nav>
            </Card>
          </div>
          
          {/* Content */}
          <div className="lg:col-span-3 space-y-6">
            {activeTab === 'profile' && (
              <>
                {profileSuccess && (
                  <Alert variant="default">
                    <AlertDescription>{profileSuccess}</AlertDescription>
                  </Alert>
                )}
                {profileError && (
                  <Alert variant="destructive">
                    <AlertDescription>{profileError}</AlertDescription>
                  </Alert>
                )}
                {photoError && (
                  <Alert variant="destructive">
                    <AlertDescription>{photoError}</AlertDescription>
                  </Alert>
                )}
                {passwordSuccess && (
                  <Alert variant="default">
                    <AlertDescription>{passwordSuccess}</AlertDescription>
                  </Alert>
                )}
                {passwordError && (
                  <Alert variant="destructive">
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}
                
                <Card padding="lg">
                  <h3 className="text-h3 font-semibold text-foreground mb-6">
                    Profile Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="relative">
                        {userProfile?.photoURL ? (
                          <img 
                            src={userProfile.photoURL} 
                            alt="Profile"
                            className="w-20 h-20 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-accent-blue rounded-full flex items-center justify-center">
                            <span className="text-primary-foreground font-bold text-2xl">{getUserInitials()}</span>
                          </div>
                        )}
                        {photoUploading && (
                          <div className="absolute inset-0 bg-background/50 rounded-full flex items-center justify-center">
                            <Spinner size="sm" />
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          id="photo-upload"
                          className="hidden"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          disabled={photoUploading}
                        />
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => document.getElementById('photo-upload')?.click()}
                          disabled={photoUploading}
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          {photoUploading ? 'Uploading...' : 'Change Avatar'}
                        </Button>
                        <p className="text-xs text-muted-foreground/70 mt-1">
                          Max 5MB (JPG, PNG, GIF)
                        </p>
                      </div>
                    </div>
                    
                    <Input
                      label="Full Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      fullWidth
                    />
                    
                    <Input
                      label="Email"
                      type="email"
                      value={currentUser?.email || ''}
                      disabled
                      fullWidth
                      helperText="Email cannot be changed"
                    />
                    
                    <div className="flex items-center justify-end gap-3 pt-4">
                      <Button 
                        variant="ghost"
                        onClick={() => setDisplayName(userProfile?.displayName || '')}
                        disabled={profileLoading}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="default"
                        onClick={handleSaveProfile}
                        disabled={profileLoading || !displayName || displayName === userProfile?.displayName}
                      >
                        {profileLoading ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>
                
                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        Change Password
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Update your password regularly for security
                      </p>
                    </div>
                    <Key className="w-5 h-5 text-accent-blue" />
                  </div>

                  <div className="space-y-4">
                    <Input
                      label="Current Password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      fullWidth
                      disabled={passwordLoading}
                    />

                    <Input
                      label="New Password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      fullWidth
                      disabled={passwordLoading}
                      helperText="Minimum 8 characters"
                    />

                    <Input
                      label="Confirm New Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      fullWidth
                      disabled={passwordLoading}
                      helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
                      className={confirmPassword && newPassword !== confirmPassword ? 'border-status-error' : ''}
                    />

                    <div className="flex justify-end pt-4">
                      <Button
                        variant="default"
                        onClick={handleChangePassword}
                        disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                      >
                        {passwordLoading ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Updating...
                          </>
                        ) : (
                          'Update Password'
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        Appearance
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Customize your interface theme
                      </p>
                    </div>
                    <Palette className="w-5 h-5 text-primary" />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-card rounded border border-border">
                    <div>
                      <div className="font-medium text-foreground">Theme Mode</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Current: <Badge variant="secondary">{theme}</Badge>
                      </div>
                    </div>
                    <Button variant="secondary" onClick={toggleTheme}>
                      Switch to {theme === 'dark' ? 'Light' : 'Dark'}
                    </Button>
                  </div>
                </Card>

                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        Time Zone
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        All dates across the portal — dashboards, reports, and exports — render in this zone.
                      </p>
                    </div>
                    <Clock className="w-5 h-5 text-primary" />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-card rounded border border-border">
                    <div>
                      <div className="font-medium text-foreground">Display time zone</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {savedTimezone === AUTO_TIME_ZONE
                          ? <>Following your device — currently <Badge variant="secondary">{zoneLabel}</Badge></>
                          : <>Showing times in <Badge variant="secondary">{zoneLabel}</Badge></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {tzSaving && <Spinner size="sm" />}
                      <TimeZonePicker
                        value={savedTimezone}
                        onChange={handleTimezoneChange}
                        extraOptions={[{ value: AUTO_TIME_ZONE, label: 'Auto (device timezone)' }]}
                        className="min-w-[220px]"
                      />
                    </div>
                  </div>
                </Card>
              </>
            )}
            
            {activeTab === 'organization' && (
              <>
                {/* Super admin with no org selected — show inline org picker */}
                {isSuperAdmin && !activeOrganization ? (
                  <Card padding="lg">
                    <h3 className="text-h3 font-semibold text-foreground mb-1">
                      Select an Organization
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Choose an organization to view and manage its settings.
                    </p>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search organizations..."
                        value={orgPickerSearch}
                        onChange={e => setOrgPickerSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    {orgPickerLoading ? (
                      <div className="flex justify-center py-8"><Spinner /></div>
                    ) : (
                      <div className="max-h-72 overflow-y-auto space-y-1">
                        {orgPickerList
                          .filter(o => o.name.toLowerCase().includes(orgPickerSearch.toLowerCase()))
                          .map(org => (
                            <div
                              key={org.id}
                              className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-muted-foreground shrink-0" />
                                <span className="text-sm text-foreground">{org.name}</span>
                              </div>
                              <ViewAsOrgButton organizationId={org.id} organizationName={org.name} compact />
                            </div>
                          ))}
                        {orgPickerList.filter(o => o.name.toLowerCase().includes(orgPickerSearch.toLowerCase())).length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-6">No organizations found</p>
                        )}
                      </div>
                    )}
                  </Card>
                ) : (
                  <>
                    {/* Super admin with org selected — Switch Organization control */}
                    {isSuperAdmin && (
                      <div className="flex justify-end mb-2">
                        {showOrgPicker ? (
                          <Card padding="lg" className="w-full">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm text-muted-foreground">
                                Currently viewing: <span className="font-medium text-foreground">{activeOrganization?.name}</span>
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setShowOrgPicker(false); setOrgPickerSearch(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                            <div className="relative mb-3">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                              <input
                                type="text"
                                placeholder="Search organizations..."
                                value={orgPickerSearch}
                                onChange={e => setOrgPickerSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                              />
                            </div>
                            {orgPickerLoading ? (
                              <div className="flex justify-center py-8"><Spinner /></div>
                            ) : (
                              <div className="max-h-72 overflow-y-auto space-y-1">
                                {orgPickerList
                                  .filter(o => o.name.toLowerCase().includes(orgPickerSearch.toLowerCase()))
                                  .map(org => (
                                    <div
                                      key={org.id}
                                      className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted transition-colors"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Building className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <span className="text-sm text-foreground">{org.name}</span>
                                      </div>
                                      <ViewAsOrgButton organizationId={org.id} organizationName={org.name} compact />
                                    </div>
                                  ))}
                                {orgPickerList.filter(o => o.name.toLowerCase().includes(orgPickerSearch.toLowerCase())).length === 0 && (
                                  <p className="text-sm text-muted-foreground text-center py-6">No organizations found</p>
                                )}
                              </div>
                            )}
                          </Card>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setShowOrgPicker(true)}>
                            Switch Organization
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Full org management panel */}
                    {!showOrgPicker && <OrgSettingsPanel />}
                  </>
                )}
              </>
            )}
            
            {activeTab === 'billing' && activeOrganization && (
              <BillingSettingsPanel />
            )}

            {activeTab === 'notifications' && (
              <Card padding="lg">
                <h3 className="text-h3 font-semibold text-foreground mb-6">
                  Notification Preferences
                </h3>
                
                <div className="space-y-4">
                  {[
                    { label: 'Flow execution completed', checked: true },
                    { label: 'Flow execution failed', checked: true },
                    { label: 'Modem goes offline', checked: true },
                    { label: 'Weekly summary report', checked: false },
                    { label: 'New team member joins', checked: false },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 bg-card rounded border border-border"
                    >
                      <span className="text-foreground">{item.label}</span>
                      <input
                        type="checkbox"
                        defaultChecked={item.checked}
                        className="w-5 h-5 rounded border-border bg-background
                                 checked:bg-accent-blue checked:border-accent-blue
                                 focus:ring-2 focus:ring-accent-blue focus:ring-offset-0"
                      />
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {activeTab === 'two-factor' && (
              <Card padding="lg">
                <TwoFactorPanel />
              </Card>
            )}

            {activeTab === 'security' && (
              <>
                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        API Keys
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Manage API keys for programmatic access to SimCheck.ai
                      </p>
                    </div>
                    <Key className="w-5 h-5 text-primary" />
                  </div>

                  {apiKeysError && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{apiKeysError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-3">
                    {apiKeysLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <Spinner size="md" />
                        <span className="ml-2 text-muted-foreground">Loading API keys...</span>
                      </div>
                    ) : apiKeys.length === 0 ? (
                      <div className="text-center p-8 border border-dashed rounded-lg">
                        <Key className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">No API keys yet</p>
                        <p className="text-sm text-muted-foreground/70 mb-4">
                          Create an API key to access SimCheck.ai programmatically
                        </p>
                      </div>
                    ) : (
                      apiKeys.map((key) => (
                        <div
                          key={key.id}
                          className="flex items-center justify-between p-4 bg-card rounded border border-border"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground truncate">
                                {key.name}
                              </span>
                              <Badge
                                variant={key.status === 'active' ? 'success' : 'destructive'}
                              >
                                {key.status}
                              </Badge>
                              {key.isTestKey && (
                                <Badge variant="outline">Test</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="text-sm text-muted-foreground/70 font-mono">
                                {key.keyPrefix}••••••••
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleCopyKeyPrefix(key.keyPrefix, key.id)}
                              >
                                {copiedKeyId === key.id ? (
                                  <Check className="w-3 h-3 text-status-success" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground/70">
                              <span>Created: {formatDate(key.createdAt)}</span>
                              {key.lastUsedAt && (
                                <span>Last used: {formatDate(key.lastUsedAt)}</span>
                              )}
                              {key.expiresAt && (
                                <span>Expires: {formatDate(key.expiresAt)}</span>
                              )}
                              <span>Calls: {key.usageCount.toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {key.status === 'active' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedKeyId(key.id);
                                    setRotatedKey(null);
                                    setRotateDialogOpen(true);
                                  }}
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Rotate
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => {
                                    setSelectedKeyId(key.id);
                                    setRevokeDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Revoke
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}

                    <Button
                      variant="secondary"
                      onClick={() => setCreateModalOpen(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create API Key
                    </Button>
                  </div>
                </Card>

                {/* Create API Key Modal */}
                {activeOrganization?.id && (
                  <CreateApiKeyModal
                    isOpen={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    organizationId={activeOrganization.id}
                    onSuccess={fetchApiKeys}
                  />
                )}

                {/* Revoke Confirmation Dialog */}
                <ConfirmDialog
                  isOpen={revokeDialogOpen}
                  onClose={() => {
                    setRevokeDialogOpen(false);
                    setSelectedKeyId(null);
                  }}
                  onConfirm={handleRevokeKey}
                  title="Revoke API Key"
                  description="Are you sure you want to revoke this API key? Any applications using this key will no longer be able to authenticate. This action cannot be undone."
                  confirmText="Revoke Key"
                  variant="destructive"
                  loading={actionLoading}
                />

                {/* Rotate Key Dialog */}
                <ConfirmDialog
                  isOpen={rotateDialogOpen}
                  onClose={() => {
                    setRotateDialogOpen(false);
                    setSelectedKeyId(null);
                    setRotatedKey(null);
                  }}
                  onConfirm={rotatedKey ? () => {
                    setRotateDialogOpen(false);
                    setSelectedKeyId(null);
                    setRotatedKey(null);
                  } : handleRotateKey}
                  title={rotatedKey ? "Key Rotated Successfully" : "Rotate API Key"}
                  description={
                    rotatedKey ? (
                      <div className="space-y-4">
                        <Alert className="border-status-warning bg-status-warning-bg">
                          <AlertTriangle className="h-4 w-4 text-status-warning" />
                          <AlertDescription className="text-status-warning">
                            Copy your new API key now. You won't be able to see it again!
                          </AlertDescription>
                        </Alert>
                        <div className="p-3 bg-muted rounded-lg font-mono text-sm break-all border">
                          {rotatedKey}
                        </div>
                      </div>
                    ) : (
                      "Are you sure you want to rotate this API key? A new key will be generated and the old key will stop working immediately. Make sure to update your applications with the new key."
                    )
                  }
                  confirmText={rotatedKey ? "Done" : "Rotate Key"}
                  variant={rotatedKey ? "default" : "destructive"}
                  loading={actionLoading}
                />
              </>
            )}

            {activeTab === 'webhooks' && (
              <Card padding="lg">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-h3 font-semibold text-foreground">
                      Webhooks
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Receive signed HTTPS notifications at your endpoint when events occur
                    </p>
                  </div>
                  <Webhook className="w-5 h-5 text-primary" />
                </div>
                {activeOrganization?.id ? (
                  <WebhookList organizationId={activeOrganization.id} />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Select an organization to manage webhooks.
                  </p>
                )}
              </Card>
            )}

            {activeTab === 'ai-integration' && (
              <>
                {/* MCP Keys Section */}
                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        MCP API Keys
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Create API keys for AI assistants like Claude Desktop and Cursor
                      </p>
                    </div>
                    <Bot className="w-5 h-5 text-primary" />
                  </div>
                  {activeOrganization?.id && (
                    <McpKeyList
                      organizationId={activeOrganization.id}
                      onCreateClick={() => setCreateMcpKeyModalOpen(true)}
                    />
                  )}
                </Card>

                {/* Configuration Guide */}
                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        Configuration Guide
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Set up your AI assistant to connect to SimCheck
                      </p>
                    </div>
                    <SettingsIcon className="w-5 h-5 text-primary" />
                  </div>
                  <McpConfigExamples organizationId={activeOrganization?.id} />
                </Card>

                {/* AI Chat Settings */}
                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-h3 font-semibold text-foreground">
                        AI Chat Assistant
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Built-in AI assistant for natural language testing
                      </p>
                    </div>
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The AI Chat Assistant (Ctrl+Shift+A) uses your organization's quota. No additional configuration needed.
                  </p>
                </Card>

                {/* Create MCP Key Modal */}
                {activeOrganization?.id && (
                  <CreateMcpKeyModal
                    isOpen={createMcpKeyModalOpen}
                    onClose={() => setCreateMcpKeyModalOpen(false)}
                    organizationId={activeOrganization.id}
                    onSuccess={() => {
                      // McpKeyList will auto-refresh
                    }}
                  />
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </AppShell>
  );
}



